<template>
    <LayoutMain>
        <div class="intro_section hidden" ref="main_01">
            <h1>{{ visual_data?.mainText || '' }}</h1>
            <section class="main_01" :style="{ backgroundImage: `url(${visual_data?.sub1FilePath || ''})` }">
                <p v-if="visual_data?.sub1Text || ''" v-html="(visual_data?.sub1Text || '').replace(/\r\n/g, '<br>')"></p>
                <div class="dim"></div>
            </section>
        </div>
        <section class="main_02 hidden" ref="main_02" :style="{ backgroundImage: `url(${visual_data?.sub2FilePath || ''})` }">
            <p v-if="visual_data?.sub2Text || ''" v-html="visual_data.sub2Text.replace(/\r\n/g, '<br>')"></p>
        </section>

        <section class="section_group hidden" ref="section_group">
            <section
                v-for="(item, index) in formattedBusinessData"
                :key="index"
                :class="'main_0' + (index + 3) + ' main_section-type2'"
                :ref="'main_0' + (index + 3)"
            >
                <div class="content">
                    <div class="left_area">
                        <p class="main_top">사업 영역</p>
                        <h3>{{ item.subject }}</h3>
                        <p>{{ item.text }}</p>
                        <router-link :to="'/business/' + item.link">
                            View More
                            <span>
                                <img src="@/assets/main_more_icon.png" alt="" />
                            </span>
                        </router-link>
                    </div>
                    <div class="right_area">
                        <div class="img_box">
                            <img :src="`${item.filePath}`" alt="" />
                        </div>
                    </div>
                </div>
                <div class="section_bg">
                    <img :src="`${item.filePath}`" alt="" />
                </div>
            </section>
        </section>

        <section class="swiper_section" ref="swiperSection">
            <div class="left_area">
                <div class="text_wrap">
                    <h2>제품소개</h2>
                    <p>
                        KICC의 결제 시스템은 사용자 친화적인 인터페이스와 <br class="pc-only" />
                        강력한 보안 기능을 갖추고 있습니다. <br class="pc-only" />
                        다양한 결제 수단을 지원하여 고객의 편리함을 극대화하며, <br class="pc-only" />
                        실시간 거래 모니터링으로 안전한 거래를 보장합니다.
                    </p>
                    <div class="handle_wrap">
                        <button class="prev_btn">
                            <img src="@/assets/swiper_prev.png" alt="" />
                        </button>
                        <p>
                            <span>01</span>
                            <span></span>
                            <span>03</span>
                        </p>
                        <button class="next_btn">
                            <img src="@/assets/swiper_next.png" alt="" />
                        </button>
                    </div>
                </div>
            </div>
            <div class="right_area">
                <div class="swiper">
                    <div class="swiper-wrapper">
                        <div class="swiper-slide" v-for="(product, index) in products" :key="index">
                            <div class="img_box">
                                <img
                                    :src="product.filePath ? product.filePath : this.defaultImagePath"
                                    :alt="product.subject"
                                    @error="setDefaultImage"
                                />
                            </div>
                            <div class="text_box">
                                <p class="title">{{ product.subject }}</p>
                                <p class="desc" v-html="product.description"></p>
                                <router-link :to="`/product/${product.slug}`">
                                    More View
                                    <span>
                                        <img src="@/assets/main_more_icon.png" alt="" />
                                    </span>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="section_last">
            <p>KICC 부가서비스로 비즈니스를 한 단계 업그레이드하세요.</p>
            <router-link to="/business/other_service/point">부가서비스 바로가기</router-link>
        </section>
    </LayoutMain>
    <PopupContainer :popupData="popupData" @close-popup="handlePopupClose" />
</template>

<script>
import LayoutMain from '@/layouts/LayoutMain.vue';
import { gsap } from 'gsap';
import { getMainInfo, getPopupList } from '@/api';
import defaultImagePath from '@/assets/no_image.png';
import PopupContainer from '@/components/PopupContainer.vue';
import Swiper from 'swiper';
import 'swiper/css';

import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
    components: {
        LayoutMain,
        PopupContainer,
    },
    data() {
        return {
            visual_data: {
                mainText: '',
                sub1Text: '',
                sub2Text: '',
            },
            business_data: null,
            product_data: {},
            defaultImagePath,
            popupData: [],
            mainPageActive: true,
        };
    },
    async mounted() {
        await this.getMainVisual();
        if (!this.mainPageActive) return;
        await this.getMainBusiness();
        if (!this.mainPageActive) return;
        await this.getMainProduct();
        if (!this.mainPageActive) return;
        await this.getPopupStatus();
        if (!this.mainPageActive) return;
        await this.$nextTick();
        if (!this.mainPageActive) return;

        const root = this.$refs.main_01?.closest('.main_content');
        if (!root || !this.$refs.main_02 || !this.$refs.swiperSection) return;

        this.mainAnimationContext = gsap.context(() => {
            gsap.to(this.$refs.main_01, { opacity: 1, visibility: 'visible', duration: 0.8, onComplete: this.showMain02 });

            gsap.from('.intro_section h1', { opacity: 0, duration: 1, y: 40 });
            gsap.from('.main_01', { opacity: 0, duration: 1, y: 80, delay: 0.2 });

            const defaultTlConfig = {
                pin: true,
                start: 'top top',
                scrub: 0.5,
            };

            gsap.timeline({
                scrollTrigger: {
                    ...defaultTlConfig,
                    trigger: this.$refs.main_01,
                    pinSpacing: false,
                    onEnter: () => {
                        gsap.to(this.$refs.main_01, { opacity: 1 });
                    },
                    onLeave: () => {
                        gsap.to(this.$refs.main_01, { opacity: 0 });
                    },
                    onEnterBack: () => {
                        gsap.to(this.$refs.main_01, { opacity: 1 });
                    },
                    onLeaveBack: () => {
                        gsap.to(this.$refs.main_01, { opacity: 0 });
                    },
                    snap: {
                        snapTo: 'labels',
                        duration: { min: 0.2, max: 3 },
                        delay: 0.2,
                        ease: 'power1.inOut',
                    },
                },
            })
                .addLabel('start')
                .from('.main_01 .dim', { autoAlpha: 0 })
                .from('.main_01', { clipPath: 'inset(45% 18% 25% 18% round 20px)' }, '<')
                .addLabel('color')
                .from('.main_01 > p', { autoAlpha: 0, y: 20 })
                .addLabel('end');

            gsap.timeline({
                scrollTrigger: {
                    ...defaultTlConfig,
                    trigger: this.$refs.main_02,
                    onEnter: () => {
                        gsap.to(this.$refs.main_02, { opacity: 1 });
                    },
                    onEnterBack: () => {
                        gsap.to(this.$refs.main_02, { opacity: 1 });
                    },
                    snap: {
                        snapTo: 'labels',
                        duration: { min: 0.2, max: 3 },
                        delay: 0.2,
                        ease: 'power1.inOut',
                    },
                },
            })
                .addLabel('start')
                .from(this.$refs.main_02.querySelector('p'), { autoAlpha: 0, y: 20 }, '<')
                .addLabel('end');

            gsap.timeline({
                scrollTrigger: {
                    ...defaultTlConfig,
                    trigger: this.$refs.section_group,
                    end: '+=400%',
                    // pinSpacing: false,
                    snap: {
                        snapTo: 'labels',
                        duration: { min: 0.2, max: 3 },
                        delay: 0.2,
                        ease: 'power1.inOut',
                    },
                },
            })
                .addLabel('start01')
                .from(this.$refs.main_03, { clipPath: 'inset(0% 0% 0% 0%)' })
                .addLabel('start02')
                .from(this.$refs.main_04, { clipPath: 'inset(100% 0% 0% 0%)' })
                .addLabel('start03')
                .from(this.$refs.main_05, { clipPath: 'inset(100% 0% 0% 0%)' })
                .addLabel('start04')
                .from(this.$refs.main_06, { clipPath: 'inset(100% 0% 0% 0%)' })
                .addLabel('start05')
                .from(this.$refs.main_07, { clipPath: 'inset(100% 0% 0% 0%)' })
                .addLabel('end');

            // dupliSection.forEach((section) => {
            //     gsap.timeline({
            //         scrollTrigger: {
            //             ...defaultTlConfig,
            //             trigger: section,
            //             // pinSpacing: false,
            //         },
            //     })
            //         .addLabel('start')
            //         .from(section, { clipPath: 'inset(100% 0% 0% 0%)' })
            //         .addLabel('end');
            // });
        
            const swiperSection = this.$refs.swiperSection;
            const counters = swiperSection.querySelectorAll('.handle_wrap p span');
            const prdSwiper = new Swiper(swiperSection.querySelector('.swiper'), {
                loop: true,
                slidesPerView: 1.2,
                spaceBetween: 20,
                breakpoints: {
                    1080: {
                        slidesPerView: 3,
                    },
                    768: {
                        slidesPerView: 1.8,
                    },
                    425: {
                        slidesPerView: 1.4,
                    },
                },
                on: {
                    slideChange: function () {
                        if (counters[0]) counters[0].innerText = `0${this.realIndex + 1}`;
                        if (counters[2]) counters[2].innerText = `0${this.slides.length}`;
                    },
                },
            });

            // gsap.timeline({
            //     scrollTrigger: {
            //         ...defaultTlConfig,
            //         trigger: '.swiper_section',
            //     },
            // });

            const nextButton = swiperSection.querySelector('.next_btn');
            const prevButton = swiperSection.querySelector('.prev_btn');
            const nextSlide = () => prdSwiper.slideNext();
            const prevSlide = () => prdSwiper.slidePrev();
            nextButton?.addEventListener('click', nextSlide);
            prevButton?.addEventListener('click', prevSlide);

            return () => {
                nextButton?.removeEventListener('click', nextSlide);
                prevButton?.removeEventListener('click', prevSlide);
                prdSwiper.destroy(true, true);
            };
        }, root);
    },
    watch: {
        async $route() {
            if (!this.mainPageActive || this.$route.path !== '/') return;
            await this.getMainVisual();
            await this.getMainBusiness();
            await this.getMainProduct();
            await this.getPopupStatus();
        },
    },
    methods: {
        async getMainVisual() {
            this.data = null;
            try {
                const response = await getMainInfo('visual');
                this.visual_data = response.result;
            } catch (error) {
                this.visual_data = {};
            }
        },
        async getMainBusiness() {
            this.data = null;
            try {
                const response = await getMainInfo('business');
                this.business_data = response.result;
            } catch (error) {
                this.business_data = {};
            }
        },
        async getMainProduct() {
            this.data = null;
            try {
                const response = await getMainInfo('product');
                this.product_data = response.result;
            } catch (error) {
                this.product_data = {};
            }
        },
        async getPopupStatus() {
            try {
                const response = await getPopupList(this.$route.query);
                this.popupData = response.list;
            } catch (error) {
                this.popupData = [];
            }
        },
        handlePopupClose(popupIdx) {
            this.popupData = this.popupData.filter((popup) => popup.idx !== popupIdx);
        },
        setDefaultImage(event) {
            event.target.src = this.defaultImagePath; // 기본 이미지로 대체
        },
        handleConfirm() {
            alert('확인 버튼 클릭!');
        },
        handleFreeUse() {
            alert('무료 사용 버튼 클릭!');
        },
        showMain02() {
            if (!this.mainPageActive || !this.$refs.main_02) return;
            gsap.to(this.$refs.main_02, { opacity: 1, visibility: 'visible', duration: 0.5, onComplete: this.showSectionGroup });
        },
        showSectionGroup() {
            if (!this.mainPageActive || !this.$refs.section_group) return;
            gsap.to(this.$refs.section_group, { opacity: 1, visibility: 'visible', duration: 0.5 });
        },
    },
    computed: {
        formattedBusinessData() {
            return [
                {
                    subject: this.business_data?.buss1Subject,
                    text: this.business_data?.buss1Text,
                    filePath: this.business_data?.buss1FilePath,
                    link: 'offline',
                },
                {
                    subject: this.business_data?.buss2Subject,
                    text: this.business_data?.buss2Text,
                    filePath: this.business_data?.buss2FilePath,
                    link: 'online',
                },
                {
                    subject: this.business_data?.buss3Subject,
                    text: this.business_data?.buss3Text,
                    filePath: this.business_data?.buss3FilePath,
                    link: 'foreigner_vat_refund',
                },
                {
                    subject: this.business_data?.buss4Subject,
                    text: this.business_data?.buss4Text,
                    filePath: this.business_data?.buss4FilePath,
                    link: 'easy_overseas_payment',
                },
                {
                    subject: this.business_data?.buss5Subject,
                    text: this.business_data?.buss5Text,
                    filePath: this.business_data?.buss5FilePath,
                    link: 'pos_system/pos',
                },
            ];
        },
        products() {
            return [
                {
                    subject: this.product_data?.product1Subject || '기본 제품 1',
                    description: (this.product_data?.product1 || '').replace(/\r\n/g, '<br />'),
                    filePath: this.product_data?.product1FilePath || '@/assets/main_swiper_dummy01.png',
                    slug: 'card_terminal',
                },
                {
                    subject: this.product_data?.product2Subject || '기본 제품 2',
                    description: (this.product_data?.product2 || '').replace(/\r\n/g, '<br />'),
                    filePath: this.product_data?.product2FilePath || '@/assets/main_swiper_dummy02.png',
                    slug: 'pos',
                },
                {
                    subject: this.product_data?.product3Subject || '기본 제품 3',
                    description: (this.product_data?.product3 || '').replace(/\r\n/g, '<br />'),
                    filePath: this.product_data?.product3FilePath || '@/assets/main_swiper_dummy03.png',
                    slug: 'kiosk',
                },
                {
                    subject: this.product_data?.product4Subject || '기본 제품 4',
                    description: (this.product_data?.product4 || '').replace(/\r\n/g, '<br />'),
                    filePath: this.product_data?.product4FilePath || '@/assets/main_swiper_dummy04.png',
                    slug: 'signpad',
                },
            ];
        },
    },
    beforeUnmount() {
        this.mainPageActive = false;
        this.mainAnimationContext?.revert();
        gsap.killTweensOf([this.$refs.main_02, this.$refs.section_group].filter(Boolean));
    },
};
</script>

<style lang="scss" scoped>
.hidden {
    opacity: 0;
    visibility: hidden;
}

.intro_section {
    position: relative;
    width: 100%;
    height: 100vh;
    background: #f4f6f6;
    z-index: 2;

    h1 {
        display: block;
        font-size: 96px;
        font-weight: 700;
        font-family: 'Play', sans-serif;
        color: #000;
        position: absolute;
        top: 25%;
        left: 50%;
        transform: translate(-50%, -50%);
        // white-space: nowrap;
        text-align: center;
        width: 100%;
        padding: 0 5vw;

        @media screen and (max-width: 1280px) {
            font-size: 6vw;
        }
    }
}

.section_group {
    height: 100vh;

    .main_section-type2 {
        overflow: hidden;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
    }
}

.main_01 {
    background: no-repeat center / cover;
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 1;

    p {
        text-align: center;
        font-size: 40px;
        font-weight: 700;
        line-height: 58px;
        color: #fff;
        position: relative;
        z-index: 1;
        text-align: center;
        width: 100%;
        padding: 0 5vw;

        @media screen and (max-width: 1024px) {
            font-size: 32px;
        }

        @media screen and (max-width: 768px) {
            font-size: 20px;
            line-height: 1.6;
        }
    }

    .dim {
        z-index: 0;
        position: absolute;
        background: #000;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.3;
    }
}

.main_02 {
    background: no-repeat center / cover;
    position: relative;
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;

    p {
        text-align: center;
        font-size: 40px;
        font-weight: 700;
        line-height: 58px;
        color: #fff;
        text-align: center;
        width: 100%;
        padding: 0 5vw;

        @media screen and (max-width: 1024px) {
            font-size: 32px;
        }

        @media screen and (max-width: 768px) {
            font-size: 20px;
            line-height: 1.6;
        }
    }

    .line_area {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 15vh;
        overflow: hidden;

        .line {
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 0;
            background: #fff;
            animation: slowDown 2s linear forwards infinite;

            &:nth-child(2) {
                animation-delay: 0.4s;
            }

            &:nth-child(3) {
                animation-delay: 0.8s;
            }

            &:nth-child(4) {
                animation-delay: 1.2s;
            }

            &:nth-child(5) {
                animation-delay: 1.6s;
            }

            &:nth-child(6) {
                animation-delay: 2s;
            }
        }
    }

    @keyframes slowDown {
        0% {
            height: 0;
            top: 0;
        }

        100% {
            height: 30px;
            top: 100%;
        }
    }
}

.main_section-type2 {
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;

    .content {
        display: flex;
        width: 100%;
        height: 100%;
        padding: 0 10%;

        @media screen and (max-width: 1024px) {
            align-items: center;
            padding: 0 5vmin;
        }

        @media screen and (max-width: 768px) {
            flex-direction: column;
            padding-top: 20vmin;
            gap: 20px;
        }
    }

    .left_area {
        flex: 1 0 0;
        display: flex;
        justify-content: center;
        flex-direction: column;

        @media screen and (max-width: 1024px) {
            padding-left: 0;
        }

        @media screen and (max-width: 768px) {
            width: 100%;
        }

        .main_top {
            font-size: 24px;
            font-weight: 600;
            color: #fff;

            @media screen and (max-width: 768px) {
                font-size: 14px;
            }
        }

        h3 {
            margin-top: 50px;
            line-height: 1;
            margin-bottom: 30px;
            font-size: 60px;
            font-weight: 700;
            color: #fff;

            @media screen and (max-width: 1024px) {
                font-size: 42px;
            }

            @media screen and (max-width: 768px) {
                font-size: 32px;
                line-height: 1.6;
                margin-bottom: 20px;
                margin-top: 0;
            }
        }

        p {
            width: 70%;
            font-size: 20px;
            color: #fff;
            font-weight: 500;
            line-height: 32px;

            @media screen and (max-width: 1024px) {
                width: 100%;
                font-size: 16px;
                line-height: 1.4;

                br {
                    display: none;
                }
            }

            @media screen and (max-width: 768px) {
                font-size: 14px;
                line-height: 1.4;
            }
        }

        a {
            margin-top: 88px;
            display: inline-flex;
            align-items: center;
            gap: 20px;
            font-family: 'Play', sans-serif;
            font-size: 18px;
            color: #fff;

            @media screen and (max-width: 768px) {
                margin-top: 40px;
            }

            span {
                border: 1px solid #fff;
                width: 100px;
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
            }
        }
    }

    .right_area {
        flex: 1 0 0;
        padding-left: 50px;
        display: flex;
        align-items: center;

        @media screen and (max-width: 768px) {
            padding-left: 0;
        }

        .img_box {
            aspect-ratio: 800/640;
            display: flex;
            justify-content: center;
            align-items: center;

            img {
                max-width: 100%;

                @media screen and (max-width: 768px) {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }

            @media screen and (max-width: 1024px) {
                width: 100%;
            }

            @media screen and (max-width: 768px) {
                aspect-ratio: 800/580;
            }
        }
    }

    &.main_03 {
        z-index: 3;
    }

    &.main_04 {
        z-index: 4;
    }

    &.main_05 {
        z-index: 5;
    }

    &.main_06 {
        z-index: 6;
    }

    &.main_07 {
        z-index: 7;
    }
}

.swiper_section {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    display: flex;
    gap: 0;

    @media screen and (max-width: 1024px) {
        height: auto;
        flex-direction: column;

        br.pc-only {
            display: none;
        }
    }

    &::after {
        content: '';
        position: absolute;
        background: #f5f5f5;
        width: 50%;
        height: 100%;
        z-index: -1;
        left: 0;
        top: 0;
        background-image: url(@/assets/main_section_logo.png);
        background-repeat: no-repeat;
        background-position: 64px 100%;
        background-size: 896px;

        @media screen and (max-width: 1024px) {
            width: 100%;
        }
    }

    .left_area {
        flex: 1 0 0;
        min-width: 0;
        padding-top: 230px;

        .text_wrap {
            position: relative;
            padding-left: 10vw;

            @media screen and (max-width: 1440px) {
                padding-left: 5vw;
            }

            @media screen and (max-width: 1024px) {
                padding-left: 0;
            }
        }

        @media screen and (max-width: 1024px) {
            padding: 15vmin 5vmin 5vmin;
            flex: 0 0 auto;
        }

        @media screen and (max-width: 425px) {
            padding: 25vmin 5vmin 10vmin;
            flex: 0 0 auto;
        }

        @media screen and (max-width: 375px) {
            padding: 25vmin 5vmin 10vmin;
            flex: 0 0 auto;
        }

        h2 {
            font-size: 60px;
            font-weight: 700;
            color: #111;
            line-height: 1;
            margin-bottom: 60px;

            @media screen and (max-width: 1440px) {
                font-size: 54px;
                margin-bottom: 48px;
            }

            @media screen and (max-width: 1080px) {
                font-size: 36px;
                margin-bottom: 32px;
            }
        }

        p {
            font-size: 20px;
            font-weight: 400;
            color: #808080;
            line-height: 32px;
            margin-bottom: 90px;

            @media screen and (max-width: 1440px) {
                font-size: 18px;
                line-height: 1.4;
            }

            @media screen and (max-width: 1080px) {
                font-size: 16px;
                margin-bottom: 30px;
            }
        }

        .handle_wrap {
            display: flex;
            align-items: center;
            gap: 30px;

            button {
                width: 20px;
                height: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
            }

            p {
                margin: 0;
                padding: 0;
                line-height: 1;

                span {
                    font-family: 'Play', sans-serif;
                    font-weight: 700;
                    font-size: 16px;
                    color: #999;

                    &:first-child {
                        color: var(--red);
                    }

                    &:nth-child(2) {
                        display: inline-block;
                        width: 1px;
                        height: 12px;
                        margin: 0 10px;
                        background: #b3b3b3;
                    }
                }
            }
        }
    }

    .right_area {
        flex: 1 0 0;
        min-width: 0;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        position: relative;

        @media screen and (max-width: 1024px) {
            align-items: flex-start;
            padding: 0 5vw 15vw;
            flex: 0;
        }

        @media screen and (max-width: 375px) {
            align-items: flex-start;
            padding: 0 5vw 10vw;
        }

        .swiper {
            position: absolute;
            width: 75vw;
            left: calc(((75vw - 60px) / 3) / 2 * -1);
            // height: 680px;
            padding: 40px 0;

            @media screen and (max-width: 1600px) {
                left: calc(((75vw - 60px) / 3) / 3 * -1);
            }

            @media screen and (max-width: 1440px) {
                width: 80vw;
                height: 600px;
                top: 10vw;
                left: calc(((80vw - 60px) / 3) / 3 * -1);
            }

            @media screen and (max-width: 1024px) {
                position: relative;
                width: 100%;
                height: 100%;
                top: auto;
                left: auto;
                overflow: visible;
                padding: 0;
            }

            @media screen and (max-width: 425px) {
                width: 140vw;
            }

            @media screen and (max-width: 425px) {
                width: 100%;
            }

            .swiper-slide {
                bottom: 0;
                transition: 0.3s ease-in-out;
                display: flex;
                flex-direction: column;
                padding: 20px;
                background: #fff;
                border: 1px solid #e5e5e5;

                @media screen and (max-width: 1024px) {
                    height: 100%;
                    min-width: 0;
                    padding: 4vmin 2vmin;
                }

                .img_box {
                    width: 100%;
                    aspect-ratio: 455/345;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 20px;

                    @media screen and (max-width: 1024px) {
                        aspect-ratio: initial;
                        height: 140px;
                    }

                    img {
                        max-width: 100%;

                        @media screen and (max-width: 1024px) {
                            width: 100%;
                            height: 100%;
                            object-fit: scale-down;
                        }
                    }
                }

                .text_box {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    padding: 0 40px 20px;

                    @media screen and (max-width: 1024px) {
                        padding: 0 2vmin 1vmin;
                        gap: 32px;
                    }

                    .title {
                        font-size: 19px;
                        font-weight: 700;
                        line-height: 1;
                        color: #111;
                        margin-bottom: 32px;

                        @media screen and (max-width: 1024px) {
                            font-size: 16px;
                            margin-bottom: 0;
                        }
                    }

                    .desc {
                        color: #808080;
                        font-size: 16px;
                        font-weight: 400;
                        display: -webkit-box;
                        -webkit-line-clamp: 4; /* 3줄 */
                        margin-bottom: 16px;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        @media screen and (max-width: 768px) {
                            font-size: 14px;
                        }
                    }

                    a {
                        margin-top: auto;
                        display: inline-flex;
                        align-items: center;
                        gap: 20px;
                        font-family: 'Play', sans-serif;
                        font-size: 18px;
                        color: #111;

                        @media screen and (max-width: 1024px) {
                            margin-top: auto;
                            font-size: 14px;
                        }

                        span {
                            border: 1px solid #fafafa;
                            background: #fafafa;
                            width: 100px;
                            height: 40px;
                            display: flex;
                            justify-content: center;
                            align-items: center;

                            img {
                                filter: invert(1);
                            }

                            @media screen and (max-width: 1024px) {
                                width: 60px;
                                height: 32px;
                            }

                            @media screen and (max-width: 768px) {
                                width: 42px;
                                height: 24px;

                                img {
                                    width: 18px;
                                }
                            }
                        }
                    }
                }

                &-active {
                    bottom: 40px;
                    border: 2px solid var(--red);
                    box-shadow: 0 0 18px rgba(230, 28, 57, 0.3);

                    @media screen and (max-width: 1024px) {
                        bottom: 0;
                    }

                    .text_box {
                        a {
                            color: #111;

                            span {
                                border: 1px solid var(--red);
                                background: var(--red);

                                img {
                                    filter: none;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

.section_last {
    width: 100%;
    aspect-ratio: 1920/500;
    background: url(@/assets/main_bottom_banner.png) no-repeat center / cover;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 30px;

    @media screen and (max-width: 1280px) {
        aspect-ratio: 1280/500;
    }

    @media screen and (max-width: 768px) {
        aspect-ratio: 1/0.5;
    }

    @media screen and (max-width: 425px) {
        aspect-ratio: 1/0.7;
    }

    p {
        color: #fff;
        font-size: 36px;
        font-weight: 700;
        text-align: center;
        word-break: keep-all;

        @media screen and (max-width: 1280px) {
            font-size: 30px;
        }

        @media screen and (max-width: 768px) {
            font-size: 24px;
        }

        @media screen and (max-width: 425px) {
            font-size: 20px;
        }
    }

    a {
        font-weight: 700;
        font-size: 18px;
        color: #fff;
        background: var(--red);
        padding: 0 35px;
        height: 50px;
        display: flex;
        justify-content: center;
        align-items: center;

        @media screen and (max-width: 425px) {
            padding: 3vw 6vw;
            font-size: 14px;
        }
    }
}

.section_bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: scale(1.2, 1.2);

    img {
        width: 100%;
        height: 100%;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        position: absolute;
        object-fit: cover;
        filter: blur(10px) brightness(0.5);
    }
}
</style>
